import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Crown } from 'lucide-react';
import * as Icons from 'lucide-react';
import { connectorPlugins } from '../../data/connectorPlugins';
interface ConnectorPluginsPanelProps {
  connectorId: string | null;
  open: boolean;
  onClose: () => void;
}
export function ConnectorPluginsPanel({
  connectorId,
  open,
  onClose
}: ConnectorPluginsPanelProps) {
  const [activePlugins, setActivePlugins] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (open && connectorId) {
      try {
        const stored = localStorage.getItem(`plugins_${connectorId}`);
        if (stored) {
          setActivePlugins(new Set(JSON.parse(stored)));
        }
      } catch (e) {}
    }
  }, [open, connectorId]);
  const togglePlugin = (id: string) => {
    setActivePlugins(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      try {
        localStorage.setItem(`plugins_${connectorId}`, JSON.stringify(Array.from(next)));
      } catch (e) {}
      return next;
    });
  };
  const plugins = connectorPlugins.filter(p => p.connectorId === connectorId);
  const categories = [{
    id: 'automation',
    label: 'Automatización'
  }, {
    id: 'alerts',
    label: 'Alertas'
  }, {
    id: 'insights',
    label: 'Insights'
  }, {
    id: 'sync',
    label: 'Sincronización'
  }];
  return <AnimatePresence data-id="element-3940">
      {open && connectorId && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm" data-id="element-3941" />

          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed top-0 right-0 bottom-0 w-full max-w-[400px] bg-slate-900 border-l border-slate-800 shadow-2xl z-[101] flex flex-col" data-id="element-3942">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/80 backdrop-blur-md shrink-0" data-id="element-3943">
              <div className="flex items-center gap-2" data-id="element-3944">
                <Zap size={18} className="text-indigo-400" data-id="element-3945" />
                <h2 className="text-lg font-bold text-slate-100 capitalize" data-id="element-3946">
                  Plugins de {connectorId}
                </h2>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors" title="Cerrar" data-id="element-3947">
                <X size={18} data-id="element-3948" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar" data-id="element-3949">
              {plugins.length === 0 ? <p className="text-sm text-slate-500 text-center py-8" data-id="element-3950">
                  No hay plugins disponibles para este conector.
                </p> : categories.map(cat => {
            const catPlugins = plugins.filter(p => p.category === cat.id);
            if (catPlugins.length === 0) return null;
            return <div key={cat.id} className="space-y-4" data-id="element-3951">
                      <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider" data-id="element-3952">
                        {cat.label}
                      </h3>
                      <div className="grid grid-cols-1 gap-3" data-id="element-3953">
                        {catPlugins.map(plugin => {
                  const Icon = (Icons as any)[plugin.icon] || Icons.Box;
                  const isActive = activePlugins.has(plugin.id);
                  return <div key={plugin.id} className={`p-4 rounded-xl border transition-all ${isActive ? 'bg-indigo-900/20 border-indigo-500/50' : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'}`} data-id="element-3954">
                              <div className="flex items-start justify-between gap-3" data-id="element-3955">
                                <div className={`p-2 rounded-lg shrink-0 ${isActive ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-900 text-slate-400'}`} data-id="element-3956">
                                  <Icon size={18} data-id="element-3957" />
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-3958">
                                  <div className="flex items-center gap-2 mb-1" data-id="element-3959">
                                    <h4 className="text-sm font-bold text-slate-200 truncate" data-id="element-3960">
                                      {plugin.name}
                                    </h4>
                                    {plugin.isPremium && <Crown size={12} className="text-amber-400" data-id="element-3961" />}
                                    {plugin.isNew && <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-[9px] font-bold rounded-full uppercase tracking-wider" data-id="element-3962">
                                        New
                                      </span>}
                                  </div>
                                  <p className="text-xs text-slate-400 mb-3 line-clamp-2" data-id="element-3963">
                                    {plugin.description}
                                  </p>
                                  <div className="flex items-center justify-between" data-id="element-3964">
                                    <span className="text-[10px] text-slate-500 font-medium" data-id="element-3965">
                                      {plugin.isPremium ? 'Premium' : 'Gratis'}
                                    </span>
                                    <button onClick={() => togglePlugin(plugin.id)} className={`relative w-11 h-6 rounded-full transition-colors ${isActive ? 'bg-indigo-600' : 'bg-slate-700'}`} data-id="element-3966">
                                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isActive ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-3967" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>;
                })}
                      </div>
                    </div>;
          })}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}